import LoadingItem from "@/components/LoadingItem";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { environment } from "@/config/env";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LanguageSelector } from "@/components/LanguageSelector";
import { RoomDetailModal } from "../components/checkout/RoomDetailModal";
import { ModeToggle } from "@/components/ThemeToggle";
import MinisiteCover from "@/components/minisite/Cover";
import MinisiteSidebar from "@/components/minisite/Sidebar";
import MinisiteCheckout from "@/components/minisite/Checkout";
import MinisiteNotes from "@/components/minisite/Notes";
import MinisiteStrengths from "@/components/minisite/Strengths";
import MinisiteVideo from "@/components/minisite/Video";
import MinisiteDescription from "@/components/minisite/Description";
import MinisiteServices from "@/components/minisite/Services";
import MinisiteMap from "@/components/minisite/Map";
import MinisiteLinks from "@/components/minisite/Links";
import {
  AlertCircle,
  ArrowRight,
  Bed,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Gift,
  Globe,
  Lock,
  Mail,
  MapPin,
  PawPrint,
  Phone,
  Sparkles,
  StickyNote,
  Tag,
  Timer,
  User,
  Users,
  Utensils,
} from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

interface VariantService {
  id?: number;
  serviceCode?: string;
  description: string;
  amount: number;
  isOneTime?: boolean;
  quantity?: number;
}

interface VariantRoomBoard {
  code: string;
  description: string;
  riep?: { totale: number; prezzo_medio: number; totale_ori: number };
}

interface VariantRoomRate {
  code: string;
  description: string;
  board: VariantRoomBoard[];
}

interface VariantRoom {
  code: string;
  description: string;
  guestNumber?: number;
  rate: VariantRoomRate[];
}

interface Variant {
  id: number;
  name: string;
  checkIn?: string;
  checkOut?: string;
  variantNote?: string;
  depositValue?: number;
  depositType?: string;
  option?: boolean;
  isDelete?: boolean;
  room: VariantRoom[];
  services?: VariantService[];
  paymentCodes?: string[];
  paymentMethods?: any[];
}

function parseDateString(s?: string | null): Date | null {
  if (!s) return null;
  const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
  const parts = datePart.split("-");
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      return new Date(y, m, d);
    }
  }
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function formatVariantDeposit(v: Variant): string | null {
  const depVal =
    v.depositValue ??
    v.paymentMethods?.find((m: any) => m.requiresDeposit && m.depositValue > 0)
      ?.depositValue;
  if (!depVal || depVal <= 0) return null;

  const depType = (
    v.depositType ||
    v.paymentMethods?.find((m: any) => m.requiresDeposit && m.depositType)
      ?.depositType ||
    v.paymentMethods?.find((m: any) => m.depositType)?.depositType ||
    ""
  )
    .trim()
    .toLowerCase();

  if (depType === "nights" || depType === "night") {
    return `${depVal} ${depVal === 1 ? "notte" : "notti"}`;
  }
  if (depType === "percent") {
    return `${depVal}%`;
  }
  return `${depVal.toFixed(2)} €`;
}

function getYouTubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("/")[0];
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const id = u.pathname.split("/").pop();
      if (id) return id;
    }
  } catch {}
  return /^[a-zA-Z0-9_-]{11}$/.test(url) ? url : null;
}

function variantServicesTotal(v: Variant): number {
  return (
    v.services?.reduce(
      (sum, s) => sum + (s.amount || 0) * (s.quantity || 1),
      0,
    ) || 0
  );
}

function variantGrossTotal(v: Variant): number {
  const roomsGross =
    v.room?.reduce((sum, r) => {
      return (
        sum +
        (r.rate?.reduce((s, rt) => {
          return (
            s +
            (rt.board?.reduce((b, bd) => {
              const ori = bd.riep?.totale_ori;
              const tot = bd.riep?.totale || 0;
              return b + (ori && ori > tot ? ori : tot);
            }, 0) || 0)
          );
        }, 0) || 0)
      );
    }, 0) || 0;

  return roomsGross + variantServicesTotal(v);
}

function variantTotal(v: Variant): number {
  const roomsTotal =
    v.room?.reduce(
      (sum, r) =>
        sum +
        (r.rate?.reduce(
          (s, rt) =>
            s +
            (rt.board?.reduce((b, bd) => b + (bd.riep?.totale || 0), 0) || 0),
          0,
        ) || 0),
      0,
    ) || 0;

  return roomsTotal + variantServicesTotal(v);
}

function variantDiscount(v: Variant): number {
  const gross = variantGrossTotal(v);
  const final = variantTotal(v);
  return gross > final ? gross - final : 0;
}

function guestCount(v: Variant) {
  return v.room?.reduce((s, r) => s + (r.guestNumber || 0), 0) || 0;
}

function childrenLabel(v: Variant) {
  return (
    v.room?.reduce((s, r) => {
      return r.guestNumber && r.guestNumber > 2 ? s + (r.guestNumber - 2) : s;
    }, 0) || 0
  );
}

import { useNavigate, useParams } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function PreventiveDetail() {
  const { token } = useParams();
  const navigate = useNavigate();
  useDocumentTitle("Dettaglio Preventivo");
  const [data, setData] = useState<any>(null);
  const [structureData, setStructureData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedVariantId, setExpandedVariantId] = useState<number | null>(
    null,
  );
  const [roomModalCode, setRoomModalCode] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const handleCancelEstimate = async () => {
    if (!token) return;
    setCancelling(true);
    try {
      await axios.post("/Estimate/cancel-by-token", {
        token,
        reason: cancelReason,
      });
      setCancelModalOpen(false);
      setIsCancelled(true);
    } catch (err: any) {
      alert(
        err.response?.data?.message ||
          "Errore durante l'annullamento del preventivo",
      );
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`/Estimate/preventive-detail/${token}`)
      .then((res) => {
        if (!res.data) {
          setIsExpired(true);
          setLoading(false);
          return;
        }
        if (res.data.idPreventiveStatus === 5) {
          navigate(`/preventive-confirmation/${token}`, { replace: true });
          return;
        }
        if (res.data.idPreventiveStatus === 7) {
          setIsCancelled(true);
          setData(res.data);
          setLoading(false);
          return;
        }
        if (res.data.idPreventiveStatus === 9) {
          setIsExpired(true);
          setLoading(false);
          return;
        }
        setData(res.data);
        const structureId = (res.data as any).structureId;
        if (structureId) {
          return axios
            .get(`/webcheckin/structure/${structureId}`)
            .then((sRes) => {
              setStructureData(sRes.data);
              setLoading(false);
            });
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.response?.status === 400 || err.response?.status === 404) {
          setIsExpired(true);
        } else {
          setIsError(true);
          setErrorMessage(
            err.response?.data?.message ||
              err.message ||
              "Errore di connessione",
          );
        }
        setLoading(false);
      });
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <LoadingItem text="Preventivo in caricamento" size="large" />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 text-center">
          <CardContent className="py-10 space-y-4">
            <Timer className="mx-auto h-16 w-16 text-amber-500" />
            <h2 className="text-2xl font-bold">Link scaduto</h2>
            <p className="text-muted-foreground">
              Il link per visualizzare questo preventivo è scaduto.
            </p>
            <Button onClick={() => navigate("/")}>Torna alla home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isCancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 text-center">
          <CardContent className="py-10 space-y-4">
            <AlertCircle className="mx-auto h-16 w-16 text-rose-500" />
            <h2 className="text-2xl font-bold">Preventivo annullato</h2>
            <p className="text-muted-foreground">
              Questo preventivo è stato annullato. Se desideri ricevere una
              nuova proposta per il tuo soggiorno, contatta direttamente la
              struttura.
            </p>
            <Button onClick={() => navigate("/")}>Torna alla home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 text-center">
          <CardContent className="py-10 space-y-4">
            <AlertCircle className="mx-auto h-16 w-16 text-red-500" />
            <h2 className="text-2xl font-bold">Errore</h2>
            <p className="text-muted-foreground">{errorMessage}</p>
            <Button onClick={() => navigate("/")}>Torna alla home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const m = data.minisite;
  const s = data.settingsMinisite;
  const settings = s || {};

  const structureCtx = structureData?.structureContextData;
  const logo = structureCtx?.logo || data.structureLogo;
  const cover = settings.cover || m?.cover;

  const fontSizes: Record<number, string> = {
    1: "14px",
    2: "16px",
    3: "18px",
    4: "20px",
    5: "24px",
    6: "30px",
    7: "36px",
  };
  const baseFontSize = settings.paragraphFont
    ? (fontSizes[settings.paragraphFont] ?? "16px")
    : "16px";
  const headingSize = settings.headingFont
    ? (fontSizes[settings.headingFont] ?? "1.875rem")
    : "1.875rem";
  const titleSize = settings.titleFont
    ? (fontSizes[settings.titleFont] ?? "1.25rem")
    : "1.25rem";
  const activeVariants: Variant[] = (data.estimateVariants || []).filter(
    (v: Variant) => !v.isDelete,
  );
  const minisiteServices = Array.isArray(data.minisiteServices)
    ? data.minisiteServices.filter((service: any) => service.highlighted)
    : data.structureServices || data.additionalServices || [];
  const cheapestId =
    activeVariants.length >= 2
      ? activeVariants.reduce((best: Variant, v: Variant) =>
          variantTotal(v) < variantTotal(best) ? v : best,
        ).id
      : null;

  const ytEmbedId = m?.ytVideo ? getYouTubeEmbedUrl(m.ytVideo) : null;
  const mapSrc =
    (data.structureAddress && data.structureCity) || data.googleMapsPosition
      ? `https://maps.google.com/maps?q=${encodeURIComponent(
          data.googleMapsPosition ||
            structureCtx?.googleMapsPosition ||
            `${data.structureAddress}, ${data.structureCity}`,
        )}&z=15&output=embed`
      : null;

  return (
    <div
      className="min-h-screen flex flex-col max-w-[1400px] mx-auto select-none"
      style={{ fontSize: baseFontSize }}
    >
      {/* Header — INTATTO */}
      <div className="fixed top-3 right-3 md:top-5 md:right-5 z-50 bg-background/60 backdrop-blur-sm rounded-full p-1 flex flex-col items-center gap-1">
        <ModeToggle />
        <LanguageSelector />
      </div>

      {/* 2-COLUMN GRID LAYOUT */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(280px,0.55fr)_minmax(0,1fr)] gap-6 p-4 md:p-8">
        {/* SIDEBAR */}
        <MinisiteSidebar
          content={m?.lateralDescription}
          hotelName={structureCtx?.name || data.structureName}
          estimateId={data.id}
          estimateDate={data.createdDate}
          boxes={data.informationBoxes || []}
        />

        {/* MAIN */}
        <main className="min-w-0 space-y-6">
          <MinisiteCover
            cover={cover}
            logo={logo}
            guestName={`${data.name} ${data.surname}`}
            lang={m?.defaultLanguage || "it"}
            strengths={data.strengths || []}
            expirationDate={data.expirationDate}
            heading={settings.heading}
            onScrollToOffer={
              activeVariants.length > 0
                ? () =>
                    document
                      .getElementById("offerta")
                      ?.scrollIntoView({ behavior: "smooth" })
                : undefined
            }
          />

          <MinisiteCheckout text={m?.checkoutNotes} />

          <MinisiteNotes text={data.preventiveNotes} />

          {minisiteServices.length > 0 && (
            <section className="space-y-3">
              <h2 style={{ fontSize: headingSize }} className="font-bold">
                {m?.defaultLanguage === "en" ? "Services" : "Servizi"}
              </h2>
              <MinisiteServices
                services={minisiteServices}
                lang={m?.defaultLanguage || "it"}
              />
            </section>
          )}
          {/* Variants / offerta */}
          {activeVariants.length > 0 && (
            <motion.div
              id="offerta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="scroll-mt-24"
            >
              <h2 style={{ fontSize: headingSize }} className="font-bold mb-2">
                La nostra migliore offerta per Lei
              </h2>
              <p className="text-muted-foreground mb-6">
                Di seguito abbiamo elencato diverse opzioni per il Suo soggiorno
              </p>
              <div className="space-y-6">
                {activeVariants.map((v: Variant) => {
                  const total = variantTotal(v);
                  const grossTotal = variantGrossTotal(v);
                  const discount = variantDiscount(v);
                  const guests = guestCount(v);
                  const isBest = cheapestId === v.id;
                  const isExpanded = expandedVariantId === v.id;
                  const inDate = parseDateString(v.checkIn);
                  const outDate = parseDateString(v.checkOut);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isArrivalPassed = inDate ? inDate < today : false;
                  const nights =
                    inDate && outDate
                      ? Math.round(
                          (outDate.getTime() - inDate.getTime()) /
                            (1000 * 60 * 60 * 24),
                        )
                      : 0;

                  return (
                    <Card
                      key={v.id}
                      className={`overflow-hidden transition-all duration-200 ${
                        isBest
                          ? "border-emerald-500/60 shadow-md ring-1 ring-emerald-500/30"
                          : "border-border/80 shadow-xs hover:border-border"
                      }`}
                    >
                      <CardContent className="p-5 md:p-7 space-y-5">
                        {/* Title & Price Header Row */}
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-border/60">
                          <div className="space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3
                                style={{ fontSize: titleSize }}
                                className="font-bold tracking-tight text-foreground"
                              >
                                {v.name}
                              </h3>
                              {isBest && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[11px] font-semibold text-white shadow-xs">
                                  <Sparkles className="h-3 w-3" />
                                  Migliore offerta
                                </span>
                              )}
                              {v.option && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-semibold">
                                  <Lock className="h-3 w-3" />
                                  Opzione riservata
                                </span>
                              )}
                              {isArrivalPassed && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 border border-destructive/30 text-destructive px-2.5 py-0.5 text-[11px] font-semibold">
                                  <AlertCircle className="h-3 w-3" />
                                  Arrivo superato
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Offerta personalizzata in base alle Tue preferenze
                            </p>
                          </div>

                          <div className="flex flex-col items-start sm:items-end shrink-0">
                            {discount > 0 && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-muted-foreground line-through font-medium">
                                  {grossTotal.toFixed(2)} €
                                </span>
                                <span className="inline-flex items-center gap-1 bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/80 px-2 py-0.5 rounded-full text-xs font-bold">
                                  <Tag className="h-3 w-3" />
                                  Risparmi {discount.toFixed(2)} €
                                  {grossTotal > 0
                                    ? ` (-${Math.round((discount / grossTotal) * 100)}%)`
                                    : ""}
                                </span>
                              </div>
                            )}
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-2xl md:text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                                {total.toFixed(2)} €
                              </span>
                              <span className="text-xs text-muted-foreground font-medium">
                                IVA inclusa
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Quick Stay Info Tiles */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {v.checkIn && (
                            <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-3 border border-border/50">
                              <div className="h-9 w-9 rounded-lg bg-background border flex items-center justify-center text-primary shrink-0 shadow-2xs">
                                <CalendarDays className="h-4.5 w-4.5" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[11px] text-muted-foreground font-medium">
                                  Soggiorno ({nights}{" "}
                                  {nights === 1 ? "notte" : "notti"})
                                </p>
                                <p className="text-xs font-semibold text-foreground truncate">
                                  {inDate
                                    ? inDate.toLocaleDateString("it-IT", {
                                        day: "numeric",
                                        month: "short",
                                      })
                                    : ""}
                                  {outDate
                                    ? ` - ${outDate.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`
                                    : ""}
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-3 border border-border/50">
                            <div className="h-9 w-9 rounded-lg bg-background border flex items-center justify-center text-primary shrink-0 shadow-2xs">
                              <Users className="h-4.5 w-4.5" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-muted-foreground font-medium">
                                Ospiti
                              </p>
                              <p className="text-xs font-semibold text-foreground truncate">
                                {data.adultsNumber || 1}{" "}
                                {(data.adultsNumber || 1) === 1
                                  ? "adulto"
                                  : "adulti"}
                                {data.childrenNumber
                                  ? `, ${data.childrenNumber} ${
                                      data.childrenNumber === 1
                                        ? "bambino"
                                        : "bambini"
                                    }`
                                  : ""}
                              </p>
                            </div>
                          </div>

                          {formatVariantDeposit(v) ? (
                            <div className="flex items-center gap-3 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 p-3 border border-emerald-200/60 dark:border-emerald-800/40">
                              <div className="h-9 w-9 rounded-lg bg-white dark:bg-zinc-900 border border-emerald-300 dark:border-emerald-700/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0 shadow-2xs">
                                <CreditCard className="h-4.5 w-4.5" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[11px] text-emerald-800 dark:text-emerald-300 font-medium">
                                  Caparra richiesta
                                </p>
                                <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 truncate">
                                  {formatVariantDeposit(v)}
                                </p>
                              </div>
                            </div>
                          ) : null}
                        </div>

                        {/* Rooms & Treatments */}
                        <div className="space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                              <Bed className="h-3.5 w-3.5" />
                              Camere e Trattamento
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {v.room?.length || 0}{" "}
                              {(v.room?.length || 0) === 1
                                ? "camera"
                                : "camere"}
                            </span>
                          </div>

                          <div className="space-y-2">
                            {v.room?.map((r, i) => {
                              const firstBoard = r.rate?.[0]?.board?.[0];
                              const promoDesc = firstBoard?.riep?.promo_desc;
                              const roomServices = (firstBoard?.riep as any)
                                ?.str_servizi as string | undefined;

                              return (
                                <div
                                  key={i}
                                  className="rounded-xl border border-border/70 bg-card p-3.5 space-y-2.5"
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-foreground">
                                        {r.description || r.code || "Camera"}
                                      </span>
                                      {firstBoard && (
                                        <span className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md text-xs font-medium">
                                          <Utensils className="h-3 w-3 text-muted-foreground" />
                                          {firstBoard.description}
                                        </span>
                                      )}
                                      {r.rate?.[0]?.description && (
                                        <span className="text-xs text-muted-foreground">
                                          ({r.rate[0].description})
                                        </span>
                                      )}
                                      {promoDesc && (
                                        <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800/80 px-2 py-0.5 rounded-md text-[11px] font-bold">
                                          <Tag className="h-3 w-3" />
                                          {promoDesc}
                                        </span>
                                      )}
                                    </div>

                                    <button
                                      onClick={() =>
                                        setRoomModalCode(
                                          r.code ||
                                            r.description ||
                                            i.toString(),
                                        )
                                      }
                                      className="text-xs text-primary font-medium hover:underline self-start sm:self-auto shrink-0 bg-transparent border-none cursor-pointer"
                                    >
                                      Dettagli camera &rarr;
                                    </button>
                                  </div>

                                  {roomServices && (
                                    <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-cyan-800 dark:text-cyan-300">
                                      <span className="font-semibold flex items-center gap-1 text-[11px]">
                                        <Gift className="h-3 w-3 text-cyan-600 dark:text-cyan-400" />{" "}
                                        Servizi inclusi nella camera:
                                      </span>
                                      {roomServices
                                        .split("|")
                                        .filter(Boolean)
                                        .map((sStr, sIdx) => {
                                          const p = sStr.split(";");
                                          return (
                                            <span
                                              key={sIdx}
                                              className="bg-cyan-50 dark:bg-cyan-950/60 border border-cyan-200 dark:border-cyan-800/80 px-2 py-0.5 rounded text-[11px] font-medium"
                                            >
                                              Cod. {p[0]} (×{p[1] || 1}){" "}
                                              {p[2] && parseFloat(p[2]) > 0
                                                ? `- €${parseFloat(p[2]).toFixed(2)}`
                                                : ""}
                                            </span>
                                          );
                                        })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Servizi Inclusi ed Extra */}
                        {v.services && v.services.length > 0 && (
                          <div className="rounded-xl border bg-emerald-50/50 dark:bg-emerald-950/20 p-3.5 space-y-2 border-emerald-200/80 dark:border-emerald-800/50">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                              <Gift className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                              <span>
                                Servizi inclusi ed extra nel pacchetto:
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {v.services.map((srv, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1.5 bg-background border border-emerald-300 dark:border-emerald-700/60 text-emerald-800 dark:text-emerald-300 text-xs px-2.5 py-1 rounded-lg shadow-2xs font-medium"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                  {srv.description}
                                  {srv.quantity && srv.quantity > 1
                                    ? ` ×${srv.quantity}`
                                    : ""}
                                  {srv.amount > 0
                                    ? ` (${srv.amount.toFixed(2)} €)`
                                    : " (Incluso)"}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Note della variante */}
                        {v.variantNote && (
                          <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20 p-4 space-y-2 transition-colors">
                            <div className="flex items-center gap-2 text-xs font-bold text-amber-900 dark:text-amber-200">
                              <StickyNote className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                              <span>Note e dettagli della proposta</span>
                            </div>
                            <div
                              className="text-xs text-amber-950/90 dark:text-amber-100/90 leading-relaxed break-words whitespace-pre-line"
                              dangerouslySetInnerHTML={
                                /<[a-z][\s\S]*>/i.test(v.variantNote)
                                  ? { __html: v.variantNote }
                                  : undefined
                              }
                            >
                              {!/<[a-z][\s\S]*>/i.test(v.variantNote)
                                ? v.variantNote
                                : undefined}
                            </div>
                          </div>
                        )}

                        {/* Action Bar */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setExpandedVariantId(isExpanded ? null : v.id)
                            }
                            className="gap-1.5 text-xs font-medium"
                          >
                            {isExpanded
                              ? "Nascondi riepilogo prezzi"
                              : "Mostra riepilogo dettagliato"}
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant={isArrivalPassed ? "secondary" : "default"}
                            size="default"
                            disabled={isArrivalPassed}
                            onClick={() =>
                              navigate(`/checkout/${token}`, {
                                state: {
                                  variant: v,
                                  adultsNumber: data?.adultsNumber,
                                  childrenNumber: data?.childrenNumber,
                                },
                              })
                            }
                            className="gap-2 font-semibold shadow-sm"
                          >
                            <span>
                              {isArrivalPassed
                                ? "Data di arrivo superata"
                                : "Seleziona questa opzione"}
                            </span>
                            {!isArrivalPassed && <ArrowRight className="h-4 w-4" />}
                          </Button>
                        </div>

                        {/* Detailed Price Breakdown Table */}
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            className="overflow-hidden pt-2"
                          >
                            <div className="border border-border/80 rounded-xl overflow-hidden shadow-2xs">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-muted/50 text-xs text-muted-foreground uppercase tracking-wider">
                                    <th className="text-left p-3 font-semibold">
                                      Camera / Servizio
                                    </th>
                                    <th className="text-left p-3 font-semibold">
                                      Trattamento / Dettagli
                                    </th>
                                    <th className="text-right p-3 font-semibold">
                                      Q.tà
                                    </th>
                                    <th className="text-right p-3 font-semibold">
                                      Prezzo medio
                                    </th>
                                    <th className="text-right p-3 font-semibold">
                                      Prezzo totale
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border/60">
                                  {v.room?.map((r, ri) =>
                                    r.rate?.map((rt, rti) =>
                                      rt.board?.map((bd, bi) => (
                                        <tr
                                          key={`${ri}-${rti}-${bi}`}
                                          className="hover:bg-muted/20 transition-colors"
                                        >
                                          <td className="p-3 font-medium text-foreground">
                                            {r.description || r.code}
                                          </td>
                                          <td className="p-3 text-muted-foreground text-xs">
                                            {bd.description ||
                                              rt.description ||
                                              rt.code}
                                          </td>
                                          <td className="p-3 text-right">1</td>
                                          <td className="p-3 text-right">
                                            {bd.riep?.prezzo_medio?.toFixed(
                                              2,
                                            ) ?? "—"}{" "}
                                            €
                                          </td>
                                          <td className="p-3 text-right font-semibold text-foreground">
                                            {bd.riep?.totale?.toFixed(2) ?? "—"}{" "}
                                            €
                                          </td>
                                        </tr>
                                      )),
                                    ),
                                  )}
                                  {v.services?.map((srv, sidx) => (
                                    <tr
                                      key={`srv-${sidx}`}
                                      className="bg-emerald-50/30 dark:bg-emerald-950/10 hover:bg-emerald-50/50 transition-colors"
                                    >
                                      <td className="p-3 font-medium text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                                        <Gift className="h-3.5 w-3.5 text-emerald-600" />
                                        {srv.description}
                                      </td>
                                      <td className="p-3 text-muted-foreground text-xs">
                                        Servizio aggiuntivo
                                      </td>
                                      <td className="p-3 text-right">
                                        {srv.quantity || 1}
                                      </td>
                                      <td className="p-3 text-right">
                                        {srv.amount > 0
                                          ? `${srv.amount.toFixed(2)} €`
                                          : "Incluso"}
                                      </td>
                                      <td className="p-3 text-right font-semibold text-emerald-700 dark:text-emerald-400">
                                        {(
                                          (srv.amount || 0) *
                                          (srv.quantity || 1)
                                        ).toFixed(2)}{" "}
                                        €
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="border-t divide-y divide-border/60 bg-muted/20">
                                  {discount > 0 && (
                                    <>
                                      <tr className="text-xs text-muted-foreground">
                                        <td
                                          colSpan={4}
                                          className="p-3 text-right font-medium"
                                        >
                                          Prezzo di listino lordo
                                        </td>
                                        <td className="p-3 text-right font-medium line-through">
                                          {grossTotal.toFixed(2)} €
                                        </td>
                                      </tr>
                                      <tr className="text-xs text-emerald-700 dark:text-emerald-400 font-bold bg-emerald-50/40 dark:bg-emerald-950/20">
                                        <td
                                          colSpan={4}
                                          className="p-3 text-right"
                                        >
                                          <div className="flex items-center justify-end gap-1.5">
                                            <Tag className="h-3.5 w-3.5" />
                                            <span>
                                              Sconto riservato applicato
                                            </span>
                                          </div>
                                        </td>
                                        <td className="p-3 text-right">
                                          - {discount.toFixed(2)} €
                                        </td>
                                      </tr>
                                    </>
                                  )}
                                  <tr className="bg-muted/40 font-bold text-base">
                                    <td
                                      colSpan={4}
                                      className="p-3.5 text-right text-foreground"
                                    >
                                      Totale Offerta (IVA inclusa)
                                    </td>
                                    <td className="p-3.5 text-right text-emerald-600 dark:text-emerald-400 font-extrabold text-lg whitespace-nowrap">
                                      {total.toFixed(2)} €
                                    </td>
                                  </tr>
                                  {formatVariantDeposit(v) && (
                                    <tr className="text-xs text-muted-foreground bg-muted/10">
                                      <td
                                        colSpan={4}
                                        className="p-3 text-right font-medium"
                                      >
                                        Caparra confirmatoria richiesta al
                                        momento della prenotazione
                                      </td>
                                      <td className="p-3 text-right font-bold text-foreground">
                                        {formatVariantDeposit(v)}
                                      </td>
                                    </tr>
                                  )}
                                </tfoot>
                              </table>
                            </div>
                          </motion.div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex justify-end pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setCancelModalOpen(true)}
                >
                  Non sei interessato? Annulla il preventivo
                </Button>
              </div>
            </motion.div>
          )}

          <MinisiteVideo embedId={ytEmbedId} />

          {/* Description + Services side by side */}
          <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-4">
            <MinisiteDescription
              content={m?.description}
              title={m?.articleTitle}
              body={m?.articleBody}
            />
            <MinisiteServices
              services={data.structureServices || data.additionalServices || []}
            />
          </section>

          {/* Map */}
          <MinisiteMap src={mapSrc} />

          {/* Carousel links */}
          <MinisiteLinks
            links={data.carouselLinks || []}
            heading={settings.carouselTitle || "Informazioni utili"}
          />

          {/* Footer */}
          <div className="text-center text-sm text-muted-foreground pt-8 border-t space-y-1">
            {logo && (
              <img
                src={logo}
                alt={structureCtx?.name || data.structureName}
                className="h-10 w-auto object-contain mx-auto mb-2"
              />
            )}
            {structureCtx?.name || data.structureName ? (
              <p className="font-semibold">
                {structureCtx?.name || data.structureName}
              </p>
            ) : null}
            {(structureCtx?.address || data.structureAddress) && (
              <p>
                {structureCtx?.address || data.structureAddress}
                {structureCtx?.city || data.structureCity
                  ? `, ${structureCtx?.city || data.structureCity}`
                  : ""}
              </p>
            )}
            {structureCtx?.tel || data.structureTel ? (
              <p>{structureCtx?.tel || data.structureTel}</p>
            ) : null}
            {data.email && (
              <p>
                <Mail className="inline h-3 w-3 mr-1" />
                {data.email}
              </p>
            )}
          </div>

          <RoomDetailModal
            open={!!roomModalCode}
            onOpenChange={(v) => {
              if (!v) setRoomModalCode(null);
            }}
            roomCode={roomModalCode ?? ""}
            estimateId={data.id}
          />

          <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Annulla il preventivo</DialogTitle>
                <DialogDescription>
                  Sei sicuro di non voler procedere con questa proposta di
                  soggiorno? L'eventuale blocco della camera verrà rilasciato.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-3">
                <Label htmlFor="cancel-reason" className="text-xs font-medium">
                  Motivazione dell'annullamento (facoltativa):
                </Label>
                <textarea
                  id="cancel-reason"
                  rows={3}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="Es. Date non compatibili, cambio programma..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  variant="ghost"
                  onClick={() => setCancelModalOpen(false)}
                  disabled={cancelling}
                >
                  Indietro
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleCancelEstimate}
                  disabled={cancelling}
                >
                  {cancelling
                    ? "Annullamento in corso..."
                    : "Conferma Annullamento"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
}
